import { t } from '@muraletta/i18n';
import { request, poll, pendingJob, imageUrls, creationId, editImage, editQuota, refreshEditQuota, preparePhoto, saveCreation, readCreation, frameImages } from '@muraletta/studio-api';
import { setupWorkbench } from '@muraletta/workbench';

function initialize(root) {
  if (root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const galleryLink=document.createElement('a');galleryLink.href=(root.dataset.rootUrl||'/')+'pages/villes-du-lab';galleryLink.textContent='Découvrir les villes du Lab ↗';galleryLink.className='mura-workbench__chip';root.querySelector('.mura-workbench__bar')?.append(galleryLink);
  const find = selector => root.querySelector(selector);
  const form = find('[data-studio-form]'), title = find('[data-title-input]'), description = find('[data-description-input]');
  const upload = find('[data-reference-input]'), progress = find('[data-progress-panel]'), results = find('[data-results-panel]');
  const status = find('[data-result-status]'), suggestions = find('[data-suggestions]');
  const workbench = setupWorkbench(root);
  let photos = [], record = null, busy = false, controller, suggestionTimer, suggestionController, frameToken = 0;
  const mode = () => form.querySelector('input[name="studio-mode"]:checked').value;
  const selected = () => record?.versions[record.selectedIndex];
  function showCanvas() {
    const image = find('[data-canvas-image]');
    const isResult = root.dataset.phase === 'results' && selected();
    const photo = root.dataset.phase === 'create' && mode() === 'photo' && photos[0];
    image.hidden = !isResult && !photo; find('[data-welcome]').hidden = Boolean(isResult || photo);
    if (isResult) image.src = selected().url;
    else if (photo) image.src = photo.dataUrl;
    find('[data-canvas-title]').textContent = isResult ? `${record.title} · ${t('Version {number}', {number:record.selectedIndex + 1})}` : photo ? t('Votre photo de référence') : t('De l’idée à l’affiche');
  }
  function setPhase(phase) {
    root.dataset.phase = phase; form.hidden = phase === 'results'; results.hidden = phase !== 'results';
    find('[data-restart-button]').hidden = phase !== 'results'; showCanvas();
  }
  function createTab(name) {
    root.querySelectorAll('[data-create-tab]').forEach(button => { const active = button.dataset.createTab === name; button.setAttribute('aria-selected', String(active)); button.tabIndex = active ? 0 : -1; });
    root.querySelectorAll('[data-create-panel]').forEach(panel => { panel.hidden = panel.dataset.createPanel !== name; });
  }
  root.querySelectorAll('[data-create-tab]').forEach(button => {
    button.addEventListener('click', () => createTab(button.dataset.createTab));
    button.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', t('Home'), 'End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === t('Home') ? 'inspiration' : event.key === 'End' ? 'ambiance' : button.dataset.createTab === 'inspiration' ? 'ambiance' : 'inspiration';
      createTab(next); find(`[data-create-tab="${next}"]`).focus();
    });
  });
  root.querySelectorAll('[data-style]').forEach(button => button.addEventListener('click', () => { description.value = button.dataset.style; description.focus(); }));
  const announce = message => { find('[data-error]').hidden = !message; find('[data-error]').textContent = message || ''; };
  const persist = () => { try { if (record) saveCreation(record); } catch { status.textContent = t('Le stockage de session est indisponible. Gardez cette page ouverte.'); } };
  function setBusy(value) {
    busy = value; root.setAttribute('aria-busy', String(value));
    form.querySelectorAll('input,textarea,button').forEach(el => { el.disabled = value; });
    results.querySelectorAll('input,textarea,button,select').forEach(el => { el.disabled = value; });
    const quota = editQuota();
    find('[data-edit-button]').disabled = value || !quota.remaining;
    find('[data-edit-help]').textContent = quota.remaining ? t('{count}/5 retouches restantes · renouvelées sous 24 h', {count:quota.remaining}) : t('Limite atteinte. Réessayez dans {hours} h.', {hours:quota.hours});
    find('[data-restart-button]').disabled = value;
  }
  function setMode() {
    const photo = mode() === 'photo';
    find('[data-photo-field]').hidden = !photo;
    find('[data-place-note]').hidden = photo;
    find('[data-title-label]').textContent = photo ? t('Le titre de votre affiche') : t('Votre ville, votre lieu ou votre idée');
    title.placeholder = photo ? t('Notre été en Corse, 2025…') : t('Annecy, un jardin méditerranéen au soleil…');
    find('[data-title-help]').textContent = photo ? t('Ajoutez au moins une photo, puis un titre.') : t('Nommez un lieu ou décrivez une scène en quelques mots.');
    suggestions.hidden = true;
    showCanvas();
  }
  function renderPhotos() {
    const list = find('[data-upload-list]'); list.replaceChildren();
    photos.forEach((photo, index) => {
      const card = document.createElement('div'); card.className = 'muraletta-atelier__upload-card';
      const image = document.createElement('img'); image.src = photo.dataUrl; image.alt = t('Photo de référence {number}', {number:index + 1});
      const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = '×'; remove.setAttribute('aria-label', t('Retirer la photo {number}', {number:index + 1}));
      remove.addEventListener('click', () => { if (!busy) { photos.splice(index, 1); renderPhotos(); } });
      card.append(image, remove); list.append(card);
    });
    showCanvas();
  }
  upload.addEventListener('change', async () => {
    if (busy) return;
    const files = [...upload.files]; upload.value = ''; announce('');
    if (photos.length + files.length > 5) return announce(t('Vous pouvez ajouter jusqu’à 5 photos. Retirez une photo pour en ajouter une autre.'));
    setBusy(true);
    try { photos.push(...await Promise.all(files.map(preparePhoto))); renderPhotos(); }
    catch (error) { announce(error.message); }
    finally { setBusy(false); }
  });
  form.querySelectorAll('[name="studio-mode"]').forEach(input => input.addEventListener('change', setMode));
  title.addEventListener('input', () => {
    clearTimeout(suggestionTimer); suggestionController?.abort(); suggestions.hidden = true;
    if (mode() === 'photo' || title.value.trim().length < 2) return;
    const query = title.value.trim();
    suggestionTimer = setTimeout(async () => {
      suggestionController = new AbortController();
      try {
        const params = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '4' });
        const response = await fetch(`${root.dataset.rootUrl}search/suggest.json?${params}`, { signal: suggestionController.signal });
        const data = await response.json();
        if (query !== title.value.trim() || mode() === 'photo') return;
        suggestions.replaceChildren();
        const matches = (data.resources?.results?.products || []).filter(product => product.handle !== 'votre-affiche-sur-mesure');
        if (!matches.length) return;
        const heading = document.createElement('p'); heading.textContent = t('Déjà au catalogue — ou créez votre propre version ci-dessous'); suggestions.append(heading);
        matches.forEach(product => {
          const link = document.createElement('a'); link.href = `${root.dataset.rootUrl}products/${encodeURIComponent(product.handle)}?personnaliser=1`;
          link.textContent = product.title; suggestions.append(link);
        }); suggestions.hidden = false;
      } catch { /* Optional catalog suggestions. */ }
    }, 250);
  });
  function renderResults() {
    if (!record) return;
    setPhase('results'); progress.hidden = true;
    frameToken++; find('[data-frame-select]').value = 'none'; find('[data-frame-image]').hidden = true;
    workbench.syncFrames();
    const grid = find('[data-result-grid]'); grid.replaceChildren();
    record.versions.forEach((version, index) => {
      const label = document.createElement('label'); label.className = 'muraletta-atelier__result-card';
      const input = document.createElement('input'); input.type = 'radio'; input.name = 'studio-result'; input.checked = index === record.selectedIndex;
      input.setAttribute('aria-label', t('Choisir la version {number}', {number:index + 1}));
      const visual = document.createElement('span'), image = document.createElement('img'), caption = document.createElement('em');
      image.src = version.url; image.alt = `${t('Version {number}', {number:index + 1})} — ${record.title}`; image.loading = 'lazy';
      caption.textContent = `${t('Version {number}', {number:index + 1})}${version.instruction ? t(' · retouchée') : ''}`;
      visual.append(image, caption); label.append(input, visual); grid.append(label);
      input.addEventListener('change', () => {
        record.selectedIndex = index; persist(); frameToken++;
        find('[data-frame-select]').value = 'none'; find('[data-frame-image]').hidden = true;
        workbench.syncFrames(); showCanvas();
        status.textContent = t('Version {number} sélectionnée.', {number:index + 1});
      });
    });
    find('[data-validate-button]').textContent = root.dataset.orderingEnabled === 'true' ? t('Choisir mon format et mon cadre') : t('Conserver cette création');
    find('[data-canvas-caption]').textContent = t('{count} piste(s) à explorer · choisissez votre version préférée', {count:record.versions.length});
    setBusy(false); persist();
  }
  async function followJob(job) {
    controller = new AbortController(); setBusy(true); setPhase('loading'); progress.hidden = false;
    find('[data-progress-message]').textContent = t('L’atelier compose votre affiche. Cela peut prendre quelques minutes…');
    try {
      const response = await poll(job.jobId, { signal: controller.signal, onProgress: (_amount, phase) => { find('[data-progress]').removeAttribute('value'); find('[data-progress-message]').textContent = phase === 'generation' ? t('Création de votre affiche…') : t('Recherche et préparation de votre affiche…'); } });
      record = { title: job.title, mode: job.mode, photoCount: job.photoCount, jobId: job.jobId, creationId: creationId(response), selectedIndex: 0, versions: imageUrls(response).map(url => ({ url, creationId: creationId(response) })) };
      pendingJob(null); find('[data-resume]').hidden = true;
      renderResults(); workbench.activate('versions'); status.textContent = t('{count} proposition(s) prête(s). Choisissez votre préférée.', {count:record.versions.length});
    } catch (error) {
      if (error.terminal || error.status === 404) pendingJob(null);
      if (error.name !== 'AbortError') announce(error.message);
      progress.hidden = true; setPhase('create'); find('[data-resume]').hidden = !pendingJob();
    } finally { controller = null; setBusy(false); }
  }
  form.addEventListener('submit', async event => {
    event.preventDefault(); if (busy) return;
    announce(''); suggestions.hidden = true;
    if (pendingJob()) { announce(t('Une création est déjà en cours. Reprenez son suivi pour éviter une seconde demande.')); find('[data-resume]').hidden = false; return; }
    const name = title.value.trim();
    if (name.length < 2) { createTab('inspiration'); announce(t('Indiquez un titre, une ville ou une description.')); title.focus(); return; }
    if (mode() === 'photo' && !photos.length) { createTab('inspiration'); announce(t('Ajoutez au moins une photo pour ce mode.')); upload.focus(); return; }
    setBusy(true); controller = new AbortController();
    try {
      const body = { cityName: name };
      if (description.value.trim()) body.additionalInstructions = description.value.trim();
      if (mode() === 'photo') body.referenceImages = photos.map(photo => photo.dataUrl);
      const response = await request('/api/create-poster-async', body, { signal: controller.signal, timeout: 45000 });
      if (!response.jobId) throw new Error(t('Aucun identifiant reçu. Ne relancez pas immédiatement : la demande peut avoir été prise en compte.'));
      const job = { jobId: response.jobId, title: name, mode: mode() === 'photo' ? 'perso' : 'lieu', photoCount: mode() === 'photo' ? photos.length : 0 };
      pendingJob(job); await followJob(job);
    } catch (error) { if (error.name !== 'AbortError') announce(error.message); }
    finally { setBusy(false); }
  });
  find('[data-resume]').addEventListener('click', () => { const job = pendingJob(); if (job && !busy) { announce(''); followJob(job); } });
  find('[data-cancel-button]').addEventListener('click', () => { controller?.abort(); announce(t('Le suivi est suspendu, pas la génération. Reprenez-le sans consommer une nouvelle création.')); });
  find('[data-restart-button]').addEventListener('click', () => { if (!busy) { setPhase('create'); frameToken++; find('[data-frame-image]').hidden = true; createTab('inspiration'); title.focus(); } });
  find('[data-edit-button]').addEventListener('click', async () => {
    if (busy || !selected()) return;
    const input = find('[data-edit-input]');
    if (!input.value.trim()) { status.textContent = t('Décrivez la modification souhaitée.'); input.focus(); return; }
    setBusy(true); status.textContent = t('Retouche en cours…');
    try {
      const result = await editImage(selected().url, input.value);
      record.versions.push({ ...result, creationId: result.creationId || selected().creationId || record.creationId });
      record.selectedIndex = record.versions.length - 1; input.value = ''; renderResults(); workbench.activate('edit');
      status.textContent = t('Retouche prête. Votre version précédente reste disponible.');
    } catch (error) { status.textContent = error.message; }
    finally { await refreshEditQuota().catch(() => {}); setBusy(false); }
  });
  find('[data-frame-select]').addEventListener('change', async event => {
    const frame = event.target.value, version = selected(), token = ++frameToken;
    const image = find('[data-frame-image]'); image.hidden = true;
    find('[data-canvas-image]').hidden = false;
    if (!version || frame === 'none') return;
    status.textContent = t('Préparation de l’aperçu encadré…');
    try {
      const frames = await frameImages(version.url);
      if (token !== frameToken || selected() !== version) return;
      if (!frames[frame]) throw new Error(t('Cet aperçu de cadre n’est pas disponible.'));
      image.src = frames[frame]; image.hidden = false; find('[data-canvas-image]').hidden = true; status.textContent = t('Aperçu de mise en situation, non contractuel.');
    } catch (error) { if (token === frameToken) status.textContent = error.message; }
  });
  find('[data-validate-button]').addEventListener('click', () => {
    if (busy || !selected()) return; persist();
    if (root.dataset.orderingEnabled === 'true' && (selected().creationId || record.creationId)) window.location.assign(root.dataset.productUrl);
    else status.textContent = t('Création conservée 30 minutes dans cet onglet. Commandes personnalisées non ouvertes.');
  });
  const params = new URLSearchParams(location.search);
  if (params.get('perso') === '1') form.querySelector('[value="photo"]').checked = true;
  if (params.get('q')) title.value = params.get('q').slice(0, 120);
  setMode(); setBusy(false);
  refreshEditQuota().then(() => { if (!busy) setBusy(false); }).catch(() => {});
  const restored = readCreation();
  if (restored && !params.has('q') && !params.has('perso') && !params.has('job')) { record = restored; renderResults(); workbench.activate('versions'); status.textContent = t('Votre dernière création vous attendait ici.'); }
  find('[data-resume]').hidden = !pendingJob();
  // Resume a saved Lab request; this only reads its status and never admits a new render.
  const resumeId = params.get('job');
  if (/^[a-f0-9]{48}$/.test(resumeId || '')) {
    const pending = pendingJob();
    if (!pending || pending.jobId === resumeId) {
      const job = pending || {jobId:resumeId,title:title.value.trim() || 'Ma création',mode:'lieu',photoCount:0};
      pendingJob(job); followJob(job);
    } else { announce(t('Une création est déjà en cours. Reprenez son suivi pour éviter une seconde demande.')); }
  }
  window.addEventListener('pagehide', () => controller?.abort(), { once: true });
}
document.querySelectorAll('[data-muraletta-studio]').forEach(initialize);
document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-muraletta-studio]').forEach(initialize));
